/**
 * Central configuration for table footer totals.
 * Edit this file to enable/disable totals per table and choose which columns aggregate.
 *
 * - `sum`: add numeric values across all rows in the filtered dataset (not only the current page).
 * - `avg`: arithmetic mean (rare; use only when meaningful).
 * - `format`: how the total cell is displayed (`number`, `currency`, `compact`, `percentage`).
 * - `valueKey`: optional row field when it differs from column id (e.g. nested data).
 * - `getValue`: optional per-row extractor (for dynamic or computed columns).
 */

export type TableTotalsAggregate = 'sum' | 'avg';

export type TableTotalsFormat = 'number' | 'currency' | 'compact' | 'percentage';

export type TableTotalsColumnSpec = {
  aggregate: TableTotalsAggregate;
  format?: TableTotalsFormat;
  /** Row property to read; defaults to column id. */
  valueKey?: string;
  /** Custom value extractor (overrides valueKey). */
  getValue?: (row: Record<string, unknown>, columnId: string) => number | null | undefined;
};

export type TableTotalsDynamicColumnSpec = {
  /** Visible column ids matching this pattern participate in totals. */
  pattern: RegExp;
  aggregate: TableTotalsAggregate;
  format?: TableTotalsFormat;
  getValue: (row: Record<string, unknown>, columnId: string) => number | null | undefined;
};

export type TableTotalsTableConfig = {
  /** When false, no footer row is rendered. Default true when an entry exists. */
  enabled?: boolean;
  /** Label shown in the footer row (first label column). Default: "Total". */
  footerLabel?: string;
  /** Column id that shows `footerLabel`. Defaults to the first visible column. */
  labelColumnId?: string;
  /** Per-column totals by column id. */
  columns?: Record<string, TableTotalsColumnSpec>;
  /** Dynamic columns (e.g. product grid columns). */
  dynamicColumns?: TableTotalsDynamicColumnSpec[];
};

/** Stable ids — pass as `tableId` on TanStackDataTable / DataTable / StandardTable. */
export type TableId =
  | 'employee-details'
  | 'bench-designation-on-bench'
  | 'bench-solvers-on-bench'
  | 'my-project-insights'
  | 'team-project-insights'
  | 'my-defaulter'
  | 'employee-insights-overview-projects'
  | 'team-dri-insights'
  | 'dri-individual-details'
  | 'dri-employee-trainings-summary'
  | 'my-dri-insights'
  | 'team-dri-products'
  | 'project-details'
  | 'project-allocation-details'
  | 'org-analytics'
  | 'general-tasks-breakdown'
  | 'customer-sentiment-standard'
  | 'customer-sentiment-my-projects'
  | 'customer-sentiment-feedback';

function productDriValue(row: Record<string, unknown>, columnId: string): number {
  const product = columnId.replace(/^product_/, '');
  const products = row.products as Record<string, { total_dri?: number; dri_score?: number }> | undefined;
  const prod = products?.[product];
  if (!prod) return 0;
  const v = prod.total_dri ?? prod.dri_score ?? 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function trainingRowNumeric(row: Record<string, unknown>, key: string): number {
  const n = Number(row[key]);
  return Number.isFinite(n) ? n : 0;
}

function teamDriProductNumeric(row: Record<string, unknown>, key: string): number {
  const product = row.product as Record<string, unknown> | undefined;
  if (!product) return 0;
  const n = Number(product[key]);
  return Number.isFinite(n) ? n : 0;
}

/** Edit totals behavior here — one entry per table. */
export const TABLE_TOTALS_CONFIG: Record<TableId, TableTotalsTableConfig> = {
  'employee-details': {
    footerLabel: 'Total',
    labelColumnId: 'employee_name',
    columns: {
      allocated_revenue: { aggregate: 'sum', format: 'currency' },
      potential_hours: { aggregate: 'sum', format: 'number' },
    },
  },
  'bench-designation-on-bench': {
    footerLabel: 'Total',
    labelColumnId: 'designation',
    columns: {
      count: { aggregate: 'sum', format: 'number' },
    },
  },
  'bench-solvers-on-bench': {
    enabled: false,
  },
  'my-project-insights': {
    footerLabel: 'Total',
    labelColumnId: 'project_code',
    columns: {
      total_allocated_hours: { aggregate: 'sum', format: 'compact' },
      total_actual_hours: { aggregate: 'sum', format: 'compact' },
      project_revenue_converted: { aggregate: 'sum', format: 'compact' },
      total_allocated_revenue_converted: { aggregate: 'sum', format: 'compact' },
      recognized_revenue: { aggregate: 'sum', format: 'compact' },
      unrecognized_revenue: { aggregate: 'sum', format: 'compact' },
      recognition_percentage: { aggregate: 'avg', format: 'percentage' },
    },
  },
  'team-project-insights': {
    footerLabel: 'Total',
    labelColumnId: 'project_code',
    columns: {
      total_allocated_hours: { aggregate: 'sum', format: 'compact' },
      total_actual_hours: { aggregate: 'sum', format: 'compact' },
      project_revenue_converted: { aggregate: 'sum', format: 'compact' },
      total_allocated_revenue_converted: { aggregate: 'sum', format: 'compact' },
      recognized_revenue: { aggregate: 'sum', format: 'compact' },
      unrecognized_revenue: { aggregate: 'sum', format: 'compact' },
      recognition_percentage: { aggregate: 'avg', format: 'percentage' },
    },
  },
  'my-defaulter': {
    footerLabel: 'Total',
    labelColumnId: 'project_code',
    columns: {
      total_allocated_hours: { aggregate: 'sum', format: 'compact' },
      total_actual_hours: { aggregate: 'sum', format: 'compact' },
    },
  },
  'employee-insights-overview-projects': {
    footerLabel: 'Total',
    labelColumnId: 'project_code',
    columns: {
      total_allocation_hours: { aggregate: 'sum', format: 'number' },
      total_revenue: { aggregate: 'sum', format: 'currency' },
    },
  },
  'team-dri-insights': {
    footerLabel: 'Total',
    labelColumnId: 'employee_name',
    columns: {
      potential_hours: { aggregate: 'sum', format: 'number' },
    },
  },
  'dri-individual-details': {
    footerLabel: 'Total',
    labelColumnId: 'individual_email',
    dynamicColumns: [
      {
        pattern: /^product_/,
        aggregate: 'sum',
        format: 'number',
        getValue: productDriValue,
      },
    ],
  },
  'dri-employee-trainings-summary': {
    footerLabel: 'Total',
    labelColumnId: 'employee_email',
    columns: {
      dri_score_avg: { aggregate: 'avg', format: 'number', getValue: (r) => trainingRowNumeric(r, 'dri_score_avg') },
      dri_score_highest: { aggregate: 'avg', format: 'number', getValue: (r) => trainingRowNumeric(r, 'dri_score_highest') },
    },
  },
  'my-dri-insights': {
    footerLabel: 'Total',
    labelColumnId: 'product_name',
    columns: {
      total_dri: { aggregate: 'sum', format: 'number' },
      dri_through_technical_certification: { aggregate: 'sum', format: 'number' },
      dri_through_labs: { aggregate: 'sum', format: 'number' },
      dri_through_project_deployed: { aggregate: 'sum', format: 'number' },
      dri_through_skill_badges: { aggregate: 'sum', format: 'number' },
      dri_through_other_cloud_experience: { aggregate: 'sum', format: 'number' },
      dri_through_technical_training: { aggregate: 'sum', format: 'number' },
    },
  },
  'team-dri-products': {
    footerLabel: 'Total',
    labelColumnId: 'member_email',
    columns: {
      total_dri: {
        aggregate: 'sum',
        format: 'number',
        getValue: (r, colId) => teamDriProductNumeric(r, colId),
      },
      dri_through_technical_certification: {
        aggregate: 'sum',
        format: 'number',
        getValue: (r, colId) => teamDriProductNumeric(r, colId),
      },
      dri_through_labs: { aggregate: 'sum', format: 'number', getValue: (r, colId) => teamDriProductNumeric(r, colId) },
      dri_through_project_deployed: {
        aggregate: 'sum',
        format: 'number',
        getValue: (r, colId) => teamDriProductNumeric(r, colId),
      },
      dri_through_skill_badges: {
        aggregate: 'sum',
        format: 'number',
        getValue: (r, colId) => teamDriProductNumeric(r, colId),
      },
      dri_through_other_cloud_experience: {
        aggregate: 'sum',
        format: 'number',
        getValue: (r, colId) => teamDriProductNumeric(r, colId),
      },
      dri_through_technical_training: {
        aggregate: 'sum',
        format: 'number',
        getValue: (r, colId) => teamDriProductNumeric(r, colId),
      },
    },
  },
  'project-details': {
    footerLabel: 'Total',
    labelColumnId: 'project_code',
    columns: {
      total_estimated_efforts: { aggregate: 'sum', format: 'number' },
      allocated_hours: { aggregate: 'sum', format: 'number' },
      actual_hours: { aggregate: 'sum', format: 'number' },
      revised_estimated_hours: { aggregate: 'sum', format: 'number' },
      project_revenue_converted: { aggregate: 'sum', format: 'currency' },
      total_allocated_revenue_converted: { aggregate: 'sum', format: 'currency' },
      recognized_revenue: { aggregate: 'sum', format: 'currency' },
      unrecognized_revenue: { aggregate: 'sum', format: 'currency' },
      realized_revenue: { aggregate: 'sum', format: 'currency' },
      unrealized_revenue: { aggregate: 'sum', format: 'currency' },
    },
  },
  'project-allocation-details': {
    footerLabel: 'Total',
    labelColumnId: 'project_code',
    columns: {
      allocated_hours: { aggregate: 'sum', format: 'number' },
      actual_hours: { aggregate: 'sum', format: 'number' },
      allocated_revenue: { aggregate: 'sum', format: 'currency' },
    },
  },
  'org-analytics': {
    footerLabel: 'Total',
    labelColumnId: 'bu_name',
    columns: {
      total_employees: { aggregate: 'sum', format: 'number', valueKey: 'total_employees' },
      total_allocated_revenue: { aggregate: 'sum', format: 'currency', valueKey: 'total_allocated_revenue' },
      avg_revenue_rate: { aggregate: 'avg', format: 'currency', valueKey: 'avg_revenue_rate' },
      potential_hours: { aggregate: 'sum', format: 'number', valueKey: 'potential_hours' },
    },
  },
  'general-tasks-breakdown': {
    enabled: false,
  },
  'customer-sentiment-standard': {
    enabled: false,
  },
  'customer-sentiment-my-projects': {
    enabled: false,
  },
  'customer-sentiment-feedback': {
    enabled: false,
  },
};

export function getTableTotalsConfig(tableId: TableId | undefined): TableTotalsTableConfig | undefined {
  if (!tableId) return undefined;
  return TABLE_TOTALS_CONFIG[tableId];
}

export function isTableTotalsEnabled(tableId: TableId | undefined): boolean {
  const cfg = getTableTotalsConfig(tableId);
  if (!cfg) return false;
  return cfg.enabled !== false;
}
